import React, { ForwardedRef, forwardRef, useCallback, useState } from 'react';
import { Button, IconEnum, TextInput, Title } from '@investoro/core';
import styled from 'styled-components';
import { userApiRequests } from 'utils/api-requests/user';
import { validatePasswordFields } from 'utils/validatePasswordOptions';

import InputContainer from 'components/common/InputContainer';
import { activeNotification } from 'components/functions/activeNotification';

const Container = styled.div`
    padding: 32px 31px;
    border-radius: var(--box-border-radius);
    background: var(--color-white);
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 60px;
`;

const ButtonContainer = styled.div`
    width: fit-content;
    align-self: flex-end;
`;

interface ChangePasswordProps {
    title: string;
}

const ChangePasswordSettings = forwardRef<HTMLDivElement, ChangePasswordProps>(({ title }, ref: ForwardedRef<HTMLDivElement>) => {
    const [triggerValidation, setTriggerValidation] = useState(false);
    const [passwordFieldsState, setPasswordFieldsState] = useState({
        oldPassword: '',
        password: '',
        confirmPassword: ''
    });

    const handlePasswordChange = useCallback((fieldName: string, value: string) => {
        setPasswordFieldsState((prev) => ({
            ...prev,
            [fieldName]: value
        }));
    }, []);

    const errors = validatePasswordFields(passwordFieldsState.oldPassword, passwordFieldsState.password, passwordFieldsState.confirmPassword, { requireOldPassword: true });

    const handleSubmit = async () => {
        setTriggerValidation(true);

        const hasErrors = Object.values(errors).some((e) => e);

        if (hasErrors) {
            activeNotification('Hasło', 'Wypełnij poprawnie wszystkie pola', 'warning');

            return;
        }

        const success = await userApiRequests.changePassword(passwordFieldsState);

        if (success) {
            setPasswordFieldsState({ oldPassword: '', password: '', confirmPassword: '' });
            setTriggerValidation(false);
        }
    };

    return (
        <div ref={ref}>
            <Title>{title}</Title>
            <Container>
                <InputContainer title="Stare hasło" icon={IconEnum.BLOCKED} required>
                    <TextInput
                        type="password"
                        value={passwordFieldsState.oldPassword}
                        onChange={(e) => handlePasswordChange('oldPassword', e.target.value)}
                        placeholder="Podaj stare hasło"
                        required
                        inputValidation={() => !passwordFieldsState.oldPassword}
                        errorMessage={!passwordFieldsState.oldPassword ? 'Hasło jest wymagane' : ''}
                        triggerValidation={triggerValidation}
                    />
                </InputContainer>

                <InputContainer title="Nowe hasło" icon={IconEnum.BLOCKED} required>
                    <TextInput
                        type="password"
                        value={passwordFieldsState.password}
                        onChange={(e) => handlePasswordChange('password', e.target.value)}
                        placeholder="Min. 8 znaków, duża litera i cyfra"
                        required
                        errorMessage={errors.password}
                        inputValidation={() => !!errors.password}
                        triggerValidation={triggerValidation}
                    />
                </InputContainer>

                <InputContainer title="Potwierdź nowe hasło" icon={IconEnum.BLOCKED} required>
                    <TextInput
                        type="password"
                        value={passwordFieldsState.confirmPassword}
                        onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                        placeholder="Potwierdź nowe hasło..."
                        required
                        inputValidation={() => !!errors.confirmPassword}
                        errorMessage={errors.confirmPassword}
                        triggerValidation={triggerValidation}
                    />
                </InputContainer>

                <ButtonContainer>
                    <Button onClick={handleSubmit}>Zapisz hasło</Button>
                </ButtonContainer>

            </Container>
        </div>
    );
});

export default ChangePasswordSettings;
